import {useMemo} from 'react';

import {useRegistryProjectMembersQuery} from '../../../generated/graphql';
import {propagateErrorsContext} from '../../../util/errors';
import {useRampFlagRegistryObserverRole} from '../../../util/rampFeatureFlags';
import {isOrgRegistryProjectName} from '../../Registries/common/utils';
import {RegistryRoles} from '../../Registries/RegistrySettings/RegistryMembersTable/types';
import {
  getFinalRegistryRole,
  getRoleFromString,
} from '../../Registries/RegistrySettings/RegistryMembersTable/utils';

export const useIsUserRestrictedViewer = (
  entityName: string,
  projectName: string,
  orgName: string,
  userId: string
) => {
  const {data: flagData, loading: flagLoading} =
    useRampFlagRegistryObserverRole(orgName);
  const enableRestrictedViewerRole = flagData;

  // Skip the query if the flag is false, or the project name is invalid.
  const skipQuery =
    !enableRestrictedViewerRole || !isOrgRegistryProjectName(projectName);

  const {data: projectData, loading: projectLoading} =
    useRegistryProjectMembersQuery({
      variables: {
        entityName,
        projectName,
      },
      context: propagateErrorsContext(),
      skip: skipQuery,
    });

  const effectiveRole = useMemo(() => {
    if (skipQuery || projectLoading || flagLoading || !projectData) {
      return null;
    }

    // Get the user's direct project role.
    const directMember = projectData.project?.members?.find(
      (member: any) => member.id === userId
    )?.role?.name;
    // If role is not found then its a viewer or restricted viewer if flag is enabled
    const directMemberRole = getRoleFromString(
      directMember || '',
      enableRestrictedViewerRole
    );

    // Get the user's team roles.
    const teamRoles =
      projectData.project?.teamMembers
        ?.filter((teamMember: any) =>
          teamMember.team.members?.some((member: any) => member.id === userId)
        )
        .map((teamMember: any) => getRoleFromString(teamMember.role.name)) ||
      [];

    return getFinalRegistryRole(
      directMemberRole,
      teamRoles,
      enableRestrictedViewerRole
    );
  }, [
    skipQuery,
    projectLoading,
    flagLoading,
    projectData,
    userId,
    enableRestrictedViewerRole,
  ]);

  // Combine loading flags.
  const loading = flagLoading || projectLoading;

  return {
    isRestrictedViewer: effectiveRole === RegistryRoles.RestrictedViewer,
    loading,
  };
};
